import {gql} from '@apollo/client';

export const GET_ALL_ACTIVE_ANNOUNCEMENT = gql`
  query GetAllActiveAnnouncements {
    getAllActiveAnnouncements {
      ... on Error {
        message
        statusCode
      }
      ... on AnnouncementArrayType {
        announcement {
          id
          type
          displayType
          title
          subTitle
          imageUrl
          openUrlOnClick
          announcementUrl
          opportunity {
            id
          }
          report {
            id
          }
          publishedDate
          expiredDate
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const GET_ALL_ANNOUNCEMENTS = gql`
  query GetAllAnnouncements($type: AllAnnouncementEnum) {
    getAllAnnouncements(type: $type) {
      ... on Error {
        message
        statusCode
      }
      ... on AnnouncementArrayType {
        announcement {
          id
          type
          displayType
          imageUrl
          openUrlOnClick
          announcementUrl
          opportunity {
            id
          }
          report {
            id
          }
          audioUrl
          title
          subTitle
          publishedDate
          expiredDate
          createdAt
          updatedAt
        }
      }
    }
  }
`;

// Read query for FULL_SCREEN_POPUP announcements. Kept separate from
// GET_ANNOUNCEMENT so the extra full-screen fields can't break the shared
// banner/popup edit form if a field name differs on the backend.
export const GET_FULL_SCREEN_ANNOUNCEMENT = gql`
  query GetFullScreenAnnouncement($announcementId: ID!) {
    getAnnouncement(announcementId: $announcementId) {
      ... on Error {
        message
        statusCode
      }
      ... on AnnouncementType {
        announcement {
          id
          type
          displayType
          language
          title
          subTitle
          imageUrl
          announcementIcon
          popUpContent
          headerBadgeText
          footerText
          closeButtonLabel
          deeplinkUrl
          actionButtonLabel
          openUrlOnClick
          announcementUrl
          benefits {
            label
            icon
          }
          report {
            id
          }
          opportunity {
            id
          }
          # Instrument v2. The scalar is what an edit resubmits; the
          # federated object supplies the visible label. All six keys resolve,
          # so an IPO/startup/venture-fund popup stored as SINGLE_OPPORTUNITY
          # reads back with its original instrument.
          instrumentId
          instrument {
            ... on PublicEquity {
              id
              name
              ticker
              key
            }
            ... on Bond {
              id
              name
              ticker
              key
            }
            ... on MutualFund {
              id
              name
              ticker
              key
            }
            ... on Ipo {
              id
              name
              ticker
              key
            }
            ... on PrivateEquity {
              id
              name
              ticker
              key
            }
            ... on VentureFund {
              id
              name
              ticker
              key
              offeringName
            }
          }
          collection {
            id
          }
          popupConfig {
            dismissible
            autoCloseMs
            maxShowCount
            purpose
            subscriptionRenewal {
              planKey
              catalogTabs {
                id
                catalogKey
                isDefault
                appliedDiscount {
                  type
                  value
                  currency
                }
              }
            }
          }
          multiLang {
            language
            title
            subTitle
            actionButtonLabel
            headerBadgeText
            footerText
            closeButtonLabel
            popUpContent
            benefits {
              label
              icon
            }
            imageUrl
            announcementUrl
          }
          targetAudience {
            userIds
            userSegments
            riskProfiles
            countryCodes
            # countriesOfInterest
            transaction {
              instrumentKeys
              instrumentIds
              startDate
              endDate
              statuses
              directions
            }
            signUpTime {
              start
              end
            }
            lastAppVisit {
              start
              end
            }
            kycStatus {
              identityCheck
              documentCheck
              amlCheck
            }
          }
          publishedDate
          expiredDate
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const GET_ANNOUNCEMENT = gql`
  query GetAnnouncement($announcementId: ID!) {
    getAnnouncement(announcementId: $announcementId) {
      ... on Error {
        message
        statusCode
      }
      ... on AnnouncementType {
        announcement {
          id
          type
          imageUrl
          openUrlOnClick
          announcementUrl
          opportunity {
            id
          }
          report {
            id
          }
          # Instrument v2. The scalar is what an edit resubmits; the
          # federated object supplies the visible label. All six keys resolve,
          # so an IPO/startup/venture-fund popup stored as SINGLE_OPPORTUNITY
          # reads back with its original instrument.
          instrumentId
          instrument {
            ... on PublicEquity {
              id
              name
              ticker
              key
            }
            ... on Bond {
              id
              name
              ticker
              key
            }
            ... on MutualFund {
              id
              name
              ticker
              key
            }
            ... on Ipo {
              id
              name
              ticker
              key
            }
            ... on PrivateEquity {
              id
              name
              ticker
              key
            }
            ... on VentureFund {
              id
              name
              ticker
              key
              offeringName
            }
          }
          collection {
            id
          }
          audioUrl
          title
          subTitle
          displayType
          language
          videoUrl
          multiLang {
            language
            title
            subTitle
            imageUrl
            videoUrl
            announcementUrl
            audioUrl
            actionButtonLabel
          }
          publishedDate
          expiredDate
          targetAudience {
            userSegments
            riskProfiles
            countryCodes
            transaction {
              instrumentKeys
              instrumentIds
              startDate
              endDate
              statuses
              directions
            }
            signUpTime {
              start
              end
            }
            lastAppVisit {
              start
              end
            }
            kycStatuses
            kycStatus {
              identityCheck
              documentCheck
              amlCheck
            }
          }
          createdAt
          updatedAt
          displayType
          popupConfig {
            dismissible
            autoCloseMs
            maxShowCount
          }
          actionButtonLabel
        }
      }
    }
  }
`;

// Options for the notification form's "Related announcement" picker.
//
// The whole pool, in three requests, because there is no single query for it:
// `AllAnnouncementEnum` has only UPCOMING and EXPIRED, and the active ones
// come from their own field. Expired announcements are included — the service
// accepts any valid announcement id and derives no behaviour from its state,
// and on dev every announcement is expired, so filtering them out left the
// picker empty. The component labels each group so the author sees what they
// are attaching.
export const GET_ANNOUNCEMENT_OPTIONS = gql`
  query GetAnnouncementOptions {
    getAllActiveAnnouncements {
      ... on Error {
        message
        statusCode
      }
      ... on AnnouncementArrayType {
        announcement {
          id
          type
          title
          subTitle
          publishedDate
          expiredDate
        }
      }
    }
    upcoming: getAllAnnouncements(type: UPCOMING) {
      ... on Error {
        message
        statusCode
      }
      ... on AnnouncementArrayType {
        announcement {
          id
          type
          title
          subTitle
          publishedDate
          expiredDate
        }
      }
    }
    expired: getAllAnnouncements(type: EXPIRED) {
      ... on Error {
        message
        statusCode
      }
      ... on AnnouncementArrayType {
        announcement {
          id
          type
          title
          subTitle
          publishedDate
          expiredDate
        }
      }
    }
  }
`;
